import { useCallback, useEffect, useMemo, useRef } from 'react';

import { usePlayerEventListener, usePlayerStore } from '@air/component-player';

import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';

const END_EPSILON_SEC = 0.01;

type PlaybackRange = {
  endTime: number;
  startTime: number;
};

const isOutsideRange = (currentTime: number, { endTime, startTime }: PlaybackRange) =>
  currentTime < startTime || currentTime >= endTime - END_EPSILON_SEC;

type UseVideoCommentRangePlaybackBoundsArgs = {
  enabled?: boolean;
};

export const useVideoCommentRangePlaybackBounds = ({ enabled = true }: UseVideoCommentRangePlaybackBoundsArgs = {}) => {
  const videoElement = usePlayerStore((state) => state.videoElement);
  const playing = usePlayerStore((state) => state.playing);
  const setCurrentTime = usePlayerStore((state) => state.setCurrentTime);
  const commentRange = useAnnotationV2Store((state) => state.commentRange);
  const restartingRef = useRef(false);

  const selectedRange = useMemo<PlaybackRange | null>(() => {
    if (!enabled) {
      return null;
    }

    if (!commentRange || commentRange.startTime === commentRange.endTime) {
      return null;
    }

    return {
      startTime: Math.min(commentRange.startTime, commentRange.endTime),
      endTime: Math.max(commentRange.startTime, commentRange.endTime),
    };
  }, [commentRange, enabled]);

  const handlePlay = useCallback(() => {
    if (!videoElement || !selectedRange) {
      return;
    }

    if (isOutsideRange(videoElement.currentTime, selectedRange)) {
      restartingRef.current = true;
      setCurrentTime(selectedRange.startTime);
      videoElement.currentTime = selectedRange.startTime;
    }
  }, [selectedRange, setCurrentTime, videoElement]);

  const handleSeeked = useCallback(() => {
    if (restartingRef.current) {
      restartingRef.current = false;
    }
  }, []);

  usePlayerEventListener({
    enabled: selectedRange !== null,
    handler: handlePlay,
    location: 'useVideoCommentRangePlaybackBounds',
    type: 'play',
  });

  usePlayerEventListener({
    enabled: selectedRange !== null,
    handler: handleSeeked,
    location: 'useVideoCommentRangePlaybackBounds',
    type: 'seeked',
  });

  useEffect(() => {
    if (!videoElement || !selectedRange || !playing) {
      return;
    }

    if (isOutsideRange(videoElement.currentTime, selectedRange)) {
      restartingRef.current = true;
      setCurrentTime(selectedRange.startTime);
      videoElement.currentTime = selectedRange.startTime;
    }
  }, [playing, selectedRange, setCurrentTime, videoElement]);

  useEffect(() => {
    if (!videoElement || !selectedRange || !playing) {
      return;
    }

    let rafId: number;

    const checkBounds = () => {
      if (restartingRef.current || videoElement.seeking) {
        if (
          restartingRef.current &&
          !videoElement.seeking &&
          videoElement.currentTime < selectedRange.endTime - END_EPSILON_SEC
        ) {
          restartingRef.current = false;
        }

        rafId = requestAnimationFrame(checkBounds);
        return;
      }

      if (videoElement.currentTime >= selectedRange.endTime) {
        videoElement.pause();
        setCurrentTime(selectedRange.endTime);
        videoElement.currentTime = selectedRange.endTime;
        return;
      }

      rafId = requestAnimationFrame(checkBounds);
    };

    rafId = requestAnimationFrame(checkBounds);

    return () => cancelAnimationFrame(rafId);
  }, [playing, selectedRange, setCurrentTime, videoElement]);
};
